import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Container from '../components/common/Container';
import ActivityFeedPreview from '../components/parent/ActivityFeedPreview';
import ActivityFeedModal from '../components/parent/ActivityFeedModal';
import {
  EyeIcon,
  ChartBarIcon,
  BellIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  HeartIcon,
  ClockIcon,
  CurrencyDollarIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const ForParents: React.FC = () => {
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);

  const benefits = [
    {
      icon: EyeIcon,
      title: 'Complete Transparency',
      description: 'See exactly what your child is learning, when they\'re learning it, and how well they\'re mastering each concept.',
    },
    {
      icon: BellIcon,
      title: 'Real-Time Notifications',
      description: 'Get instant alerts about achievements, struggles, and important milestones in your child\'s education.',
    },
    {
      icon: ChartBarIcon,
      title: 'Progress at a Glance',
      description: 'Visual dashboards show mastery levels, learning streaks, and growth trends across all subjects.',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Direct Teacher Communication',
      description: 'Message teachers directly through the platform with context about specific learning areas.',
    },
    {
      icon: CalendarIcon,
      title: 'Learning Schedule',
      description: 'Track assignments, upcoming assessments, and recommended study times all in one place.',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Safe & Private',
      description: 'Bank-level security protects your child\'s data with full compliance to Australian privacy laws.',
    },
  ];

  const insights = [
    {
      title: 'Daily Activity Summary',
      description: 'What your child worked on today',
      metrics: ['Topics covered', 'Time spent', 'Questions answered', 'Skills improved'],
    },
    {
      title: 'Mastery Tracking',
      description: 'How well concepts are understood',
      metrics: ['Current mastery levels', 'Knowledge decay alerts', 'Reinforcement needs', 'Comparison to benchmarks'],
    },
    {
      title: 'Engagement Metrics',
      description: 'How engaged your child is',
      metrics: ['Learning streaks', 'Achievement badges', 'Motivation levels', 'Focus patterns'],
    },
    {
      title: 'Support Opportunities',
      description: 'Where you can help at home',
      metrics: ['Struggling topics', 'Homework guidance', 'Extension activities', 'Practice suggestions'],
    },
  ];

  const features = [
    'Live progress tracking across all subjects',
    'Instant notifications for achievements and struggles',
    'Direct messaging with teachers',
    'Homework visibility and completion tracking',
    'Learning recommendations for home',
    'Screen time and engagement monitoring',
    'Detailed weekly and term reports',
    'Mobile app for on-the-go access',
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
                Be Part of Your Child's <span className="text-gradient">Learning Journey</span>
              </h1>
              <p className="text-xl text-[var(--color-light-gray)] mb-8">
                Finally, real-time visibility into your child's education. Know exactly how they're progressing,
                where they need help, and how to support them at home.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">See progress as it happens, not quarterly</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Get specific ways to help at home</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                  <span className="text-white">Celebrate achievements together</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                  Request Parent Access
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                  See How It Works
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="card-premium p-8">
                <div className="text-center mb-6">
                  <DevicePhoneMobileIcon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Parent Portal</h3>
                  <p className="text-[var(--color-light-gray)]">Your child's learning in real-time</p>
                </div>
                <div className="space-y-4">
                  <div className="p-3 bg-gradient-to-r from-[var(--color-emerald)]/20 to-[var(--color-emerald)]/10 rounded-lg border-l-4 border-[var(--color-emerald)]">
                    <div className="text-sm text-[var(--color-emerald)] mb-1">Achievement Unlocked!</div>
                    <div className="text-white">Emma mastered multiplication tables</div>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span className="text-white">Mathematics</span>
                      <span className="text-[var(--color-emerald)]">85%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-gradient-to-r from-[var(--color-electric-blue)] to-[var(--color-emerald)] h-2 rounded-full" style={{ width: '85%' }}></div>
                    </div>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span className="text-white">English</span>
                      <span className="text-[var(--color-violet)]">92%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div className="bg-gradient-to-r from-[var(--color-electric-blue)] to-[var(--color-violet)] h-2 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Why Parents Love <span className="text-gradient">Lumen8</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Real-time visibility into your child's progress
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-8 h-full hover-glow">
                  <benefit.icon className="w-12 h-12 text-[var(--color-electric-blue)] mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                  <p className="text-[var(--color-light-gray)]">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Tutoring Comparison Section */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Better Than a <span className="text-gradient">$50/Hour Tutor</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Private tutoring can cost $50-100+ per hour. Lumen8 offers something even better — at a fraction of the cost.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full hover-glow text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] p-4 mx-auto mb-6">
                  <ClockIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Always Available</h3>
                <p className="text-[var(--color-light-gray)]">
                  No scheduling conflicts. Your child can learn at 7am before school or 9pm after dinner — whenever works best for them.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full hover-glow text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-violet)] to-[var(--color-electric-blue)] p-4 mx-auto mb-6">
                  <SparklesIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Always Rewarding</h3>
                <p className="text-[var(--color-light-gray)]">
                  Gamification keeps your child motivated with XP, achievements, and streaks. Learning becomes something they want to do, not have to do.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8 h-full hover-glow text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-emerald)] to-[var(--color-bright-cyan)] p-4 mx-auto mb-6">
                  <CurrencyDollarIcon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Fraction of the Cost</h3>
                <p className="text-[var(--color-light-gray)]">
                  One hour of tutoring per week costs ~$2,500/year. Lumen8 covers all subjects, unlimited practice, for far less.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <div className="card-premium p-8">
              <h3 className="text-xl font-bold text-white mb-4 text-center">What You Get That a Tutor Can't Offer</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--color-light-gray)]">Coverage across all subjects, not just one</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--color-light-gray)]">Instant feedback on every question</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--color-light-gray)]">AI that remembers what they struggle with</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--color-light-gray)]">Spaced repetition to prevent forgetting</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--color-light-gray)]">Real-time visibility for you as a parent</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--color-light-gray)]">Aligned directly to school curriculum</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Insights You Get */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Insights That <span className="text-gradient">Matter</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              Understand your child's learning like never before
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {insights.map((insight, index) => (
              <motion.div
                key={insight.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="glass-card p-8"
              >
                <h3 className="text-2xl font-bold text-white mb-2">{insight.title}</h3>
                <p className="text-[var(--color-electric-blue)] mb-4">{insight.description}</p>
                <div className="space-y-2">
                  {insight.metrics.map((metric) => (
                    <div key={metric} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[var(--color-emerald)]"></div>
                      <span className="text-[var(--color-light-gray)]">{metric}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Parent Dashboard Preview */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Your <span className="text-gradient">Parent Portal</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] max-w-3xl mx-auto">
              See your child's learning journey unfold in real-time
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Parent Dashboard Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center">
                <div className="card-premium overflow-hidden hover-glow">
                  <img
                    src="/parent-dashboard.png"
                    alt="Parent Dashboard"
                    className="w-full h-auto"
                    style={{ maxWidth: '340px' }}
                  />
                </div>
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold text-white">Progress Overview</h3>
                  <p className="text-sm text-[var(--color-light-gray)]">Mastery levels across all subjects</p>
                </div>
              </div>
            </motion.div>

            {/* Activity Feed Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="flex flex-col items-center">
                <ActivityFeedPreview onClick={() => setIsActivityModalOpen(true)} />
                <div className="mt-4 text-center">
                  <h3 className="text-lg font-semibold text-white">Activity Feed</h3>
                  <p className="text-sm text-[var(--color-light-gray)]">Achievements, milestones, and learning updates</p>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Features List */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)]">
        <Container>
          <div className="card-premium p-12 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">
                Everything You Need to Support Their Success
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {features.map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-[var(--color-emerald)] flex-shrink-0" />
                  <span className="text-[var(--color-light-gray)]">{feature}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 p-4 bg-gradient-to-r from-[var(--color-electric-blue)]/10 to-[var(--color-bright-cyan)]/10 rounded-lg">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-6 h-6 text-[var(--color-electric-blue)] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-white mb-1">Privacy First</div>
                  <p className="text-sm text-[var(--color-light-gray)]">
                    Your child's data is protected with bank-level encryption and full compliance with Australian privacy laws.
                    You control what information is shared and with whom.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* How to Get Started */}
      <section className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">
              Getting Started is <span className="text-gradient">Easy</span>
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold text-white mb-2">School Enrolls</h3>
                <p className="text-[var(--color-light-gray)]">
                  Your school signs up for Lumen8 and imports student data
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold text-white mb-2">You Get Access</h3>
                <p className="text-[var(--color-light-gray)]">
                  Receive your secure parent account via email invitation
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)] flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Start Engaging</h3>
                <p className="text-[var(--color-light-gray)]">
                  See real-time progress and support your child's learning journey
                </p>
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <HeartIcon className="w-16 h-16 text-[var(--color-electric-blue)] mx-auto mb-6" />
            <h2 className="text-4xl font-black text-white mb-6">
              Be Their Learning <span className="text-gradient">Champion</span>
            </h2>
            <p className="text-xl text-[var(--color-light-gray)] mb-8">
              Stay connected to your child's learning journey like never before.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-premium text-lg px-8 py-4 inline-flex items-center gap-2">
                Talk to Your School
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <Link to="/how-it-works" className="btn-glass text-lg px-8 py-4">
                Learn More
              </Link>
            </div>
            <p className="mt-8 text-sm text-[var(--color-medium-gray)]">
              Already using Lumen8? Download the parent app from your school's portal
            </p>
          </div>
        </Container>
      </section>

      {/* Activity Feed Modal */}
      <ActivityFeedModal
        isOpen={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
      />
    </>
  );
};

export default ForParents;